/**
 * RTK Query slice for `services/kiosks` (port 6400) — Phase 4 T06 builder
 * surface. Every URL here is typed against the routes documented in
 * `services/kiosks/README.md` ("Routes and policies") and the response shapes
 * projected by the service controllers (`toPublicTemplate` / `toPublicDevice`).
 *
 * Only auth-policy-gated admin/builder routes are wired. The device-key-only
 * surface (`GET /v1/kiosks/published`) is deliberately NOT consumed here —
 * it belongs to kiosk devices, not to the admin session.
 *
 * Paths follow the admin-ui per-service proxy convention: the base `/api` +
 * `/kiosks` prefix is stripped by the vite/nginx proxy, so the RTK url keeps
 * the service's own `/v1/kiosks/...` controller stem.
 */
import { apiSlice } from './apiSlice';
import type {
  KioskLayoutWidget,
  KioskRenderDocument,
  KioskTemplateVisibility,
  KioskWidgetDescriptor,
} from '@cluster/types';

export type KioskTemplateStatus = 'draft' | 'published' | 'archived';
export type KioskDeviceStatus = 'active' | 'disabled' | 'revoked';

/** One stored template version; published entries are frozen by the service. */
export interface KioskTemplateVersionRecord {
  version: number;
  layout: KioskLayoutWidget[];
  visibility: KioskTemplateVisibility;
  publishedAt?: string | null;
  publishedBy?: string | null;
}

/** Public template projection (`toPublicTemplate` in the service). */
export interface KioskTemplateRecord {
  id: string;
  orgId: string;
  name: string;
  status: KioskTemplateStatus;
  storeId?: string;
  workspaceId?: string;
  currentVersion: number;
  versions: KioskTemplateVersionRecord[];
  createdBy: string;
  updatedBy?: string;
  duplicatedFromTemplateId?: string;
  duplicatedFromVersion?: number;
  createdAt?: string;
  updatedAt?: string;
}

/** Safe device projection (`toPublicDevice`): never keyHash, never plaintext. */
export interface KioskDeviceRecord {
  id: string;
  orgId: string;
  storeId: string;
  workspaceId?: string;
  templateId?: string;
  name: string;
  apiKeyId: string;
  keyPrefix: string;
  policyExpiresAt: string;
  status: KioskDeviceStatus;
  lastSeenAt?: string | null;
  revokedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** Register/rotate responses carry the plaintext key exactly once. */
export interface KioskDeviceCredentialResult {
  device: KioskDeviceRecord;
  key: string;
}

export interface CreateKioskTemplateBody {
  name: string;
  storeId?: string;
  workspaceId?: string;
}

export interface UpdateKioskTemplateBody {
  name?: string;
  storeId?: string | null;
  workspaceId?: string | null;
}

export interface SaveKioskTemplateVersionBody {
  layout: KioskLayoutWidget[];
  visibility: KioskTemplateVisibility;
}

export interface DuplicateKioskTemplateBody {
  name?: string;
  version?: number;
}

export interface DuplicateKioskTemplateResult {
  template: KioskTemplateRecord;
  sourceVersion: number;
}

export interface RegisterKioskDeviceBody {
  name: string;
  storeId: string;
  workspaceId?: string;
  templateId?: string;
}

export interface UpdateKioskDeviceBody {
  name?: string;
  storeId?: string;
  workspaceId?: string | null;
  templateId?: string | null;
}

export interface KioskDeviceListQuery {
  storeId?: string;
  workspaceId?: string;
}

export const kiosksApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ── Templates (builder surface, user principal only) ────────────────
    getKioskTemplates: builder.query<KioskTemplateRecord[], { includeArchived?: boolean } | void>({
      query: (params) => ({
        url: '/kiosks/v1/kiosks/templates',
        params: params?.includeArchived ? { includeArchived: 'true' } : undefined,
      }),
      providesTags: (rows) =>
        rows
          ? [...rows.map(({ id }) => ({ type: 'Kiosk' as const, id: `template-${id}` })), { type: 'Kiosk', id: 'templates' }]
          : [{ type: 'Kiosk', id: 'templates' }],
    }),
    getKioskTemplate: builder.query<KioskTemplateRecord, string>({
      query: (id) => `/kiosks/v1/kiosks/templates/${encodeURIComponent(id)}`,
      providesTags: (_result, _error, id) => [{ type: 'Kiosk', id: `template-${id}` }],
    }),
    createKioskTemplate: builder.mutation<KioskTemplateRecord, CreateKioskTemplateBody>({
      query: (body) => ({ url: '/kiosks/v1/kiosks/templates', method: 'POST', body }),
      invalidatesTags: [{ type: 'Kiosk', id: 'templates' }],
    }),
    updateKioskTemplate: builder.mutation<KioskTemplateRecord, { id: string; body: UpdateKioskTemplateBody }>({
      query: ({ id, body }) => ({ url: `/kiosks/v1/kiosks/templates/${encodeURIComponent(id)}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Kiosk', id: `template-${id}` }, { type: 'Kiosk', id: 'templates' }],
    }),
    /** Draft save; after a publish this starts a NEW draft version and never touches the frozen one. */
    saveKioskTemplateVersion: builder.mutation<KioskTemplateRecord, { id: string; body: SaveKioskTemplateVersionBody }>({
      query: ({ id, body }) => ({ url: `/kiosks/v1/kiosks/templates/${encodeURIComponent(id)}/versions`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Kiosk', id: `template-${id}` }, { type: 'Kiosk', id: 'templates' }],
    }),
    /** Distinct capability; re-validates then freezes the current version (409 if already frozen). */
    publishKioskTemplate: builder.mutation<KioskTemplateRecord, string>({
      query: (id) => ({ url: `/kiosks/v1/kiosks/templates/${encodeURIComponent(id)}/publish`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Kiosk', id: `template-${id}` }, { type: 'Kiosk', id: 'templates' }],
    }),
    duplicateKioskTemplate: builder.mutation<DuplicateKioskTemplateResult, { id: string; body?: DuplicateKioskTemplateBody }>({
      query: ({ id, body }) => ({ url: `/kiosks/v1/kiosks/templates/${encodeURIComponent(id)}/duplicate`, method: 'POST', body: body ?? {} }),
      invalidatesTags: [{ type: 'Kiosk', id: 'templates' }],
    }),
    getKioskTemplatePreview: builder.query<KioskRenderDocument, { id: string; version: number }>({
      query: ({ id, version }) => `/kiosks/v1/kiosks/templates/${encodeURIComponent(id)}/preview/${version}`,
      // Read-only render document; nothing to invalidate.
    }),
    archiveKioskTemplate: builder.mutation<KioskTemplateRecord, string>({
      query: (id) => ({ url: `/kiosks/v1/kiosks/templates/${encodeURIComponent(id)}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Kiosk', id: `template-${id}` }, { type: 'Kiosk', id: 'templates' }],
    }),

    // ── Widget catalog (builder palette — the only palette source) ──────
    getKioskWidgets: builder.query<KioskWidgetDescriptor[], void>({
      query: () => '/kiosks/v1/kiosks/widgets',
      providesTags: [{ type: 'Kiosk', id: 'widgets' }],
    }),

    // ── Devices (safe metadata only; plaintext key only in register/rotate) ─
    getKioskDevices: builder.query<KioskDeviceRecord[], KioskDeviceListQuery | void>({
      query: (params) => ({
        url: '/kiosks/v1/kiosks/devices',
        params: params
          ? { ...(params.storeId ? { storeId: params.storeId } : {}), ...(params.workspaceId ? { workspaceId: params.workspaceId } : {}) }
          : undefined,
      }),
      providesTags: (rows) =>
        rows
          ? [...rows.map(({ id }) => ({ type: 'Kiosk' as const, id: `device-${id}` })), { type: 'Kiosk', id: 'devices' }]
          : [{ type: 'Kiosk', id: 'devices' }],
    }),
    getKioskDevice: builder.query<KioskDeviceRecord, string>({
      query: (id) => `/kiosks/v1/kiosks/devices/${encodeURIComponent(id)}`,
      providesTags: (_result, _error, id) => [{ type: 'Kiosk', id: `device-${id}` }],
    }),
    registerKioskDevice: builder.mutation<KioskDeviceCredentialResult, RegisterKioskDeviceBody>({
      query: (body) => ({ url: '/kiosks/v1/kiosks/devices', method: 'POST', body }),
      invalidatesTags: [{ type: 'Kiosk', id: 'devices' }],
    }),
    updateKioskDevice: builder.mutation<KioskDeviceRecord, { id: string; body: UpdateKioskDeviceBody }>({
      query: ({ id, body }) => ({ url: `/kiosks/v1/kiosks/devices/${encodeURIComponent(id)}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Kiosk', id: `device-${id}` }, { type: 'Kiosk', id: 'devices' }],
    }),
    /** Service-side lifecycle: mint replacement key/policies FIRST, then revoke the old key (README §Device credential lifecycle). */
    rotateKioskDeviceKey: builder.mutation<KioskDeviceCredentialResult, string>({
      query: (id) => ({ url: `/kiosks/v1/kiosks/devices/${encodeURIComponent(id)}/rotate-key`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Kiosk', id: `device-${id}` }, { type: 'Kiosk', id: 'devices' }],
    }),
    /** Reversible pause; credential still revoked server-side. */
    disableKioskDevice: builder.mutation<KioskDeviceRecord, string>({
      query: (id) => ({ url: `/kiosks/v1/kiosks/devices/${encodeURIComponent(id)}/disable`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Kiosk', id: `device-${id}` }, { type: 'Kiosk', id: 'devices' }],
    }),
    /** Terminal revoke; revoked devices cannot be re-enabled or rotated. */
    revokeKioskDevice: builder.mutation<KioskDeviceRecord, string>({
      query: (id) => ({ url: `/kiosks/v1/kiosks/devices/${encodeURIComponent(id)}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Kiosk', id: `device-${id}` }, { type: 'Kiosk', id: 'devices' }],
    }),
  }),
});

export const {
  useGetKioskTemplatesQuery,
  useGetKioskTemplateQuery,
  useCreateKioskTemplateMutation,
  useUpdateKioskTemplateMutation,
  useSaveKioskTemplateVersionMutation,
  usePublishKioskTemplateMutation,
  useDuplicateKioskTemplateMutation,
  useLazyGetKioskTemplatePreviewQuery,
  useArchiveKioskTemplateMutation,
  useGetKioskWidgetsQuery,
  useGetKioskDevicesQuery,
  useGetKioskDeviceQuery,
  useRegisterKioskDeviceMutation,
  useUpdateKioskDeviceMutation,
  useRotateKioskDeviceKeyMutation,
  useDisableKioskDeviceMutation,
  useRevokeKioskDeviceMutation,
} = kiosksApi;
